import { MUSCLES, REGION_LABEL, type MuscleId, type Region } from '../data/muscles';
import type { MuscleStatus } from '../engine/recovery';

const REGIONS: Region[] = ['push', 'pull', 'legs', 'core'];

function barColor(freshness: number): string {
  if (freshness >= 1) return 'var(--ok)';
  if (freshness >= 0.75) return 'var(--accent)';
  if (freshness >= 0.5) return 'var(--warn)';
  return 'var(--danger)';
}

function label(status: MuscleStatus): string {
  if (status.daysSince === undefined) return 'never';
  if (status.freshness >= 1) return 'ready';
  return `${Math.round(status.freshness * 100)}%`;
}

/**
 * The "what's recovered" view. Each bar is how far through that muscle's
 * recovery window you are since the last time you trained it directly — full
 * bar means it's ready for another hard session.
 */
export function RecoveryGrid({
  statuses,
  regions = REGIONS,
}: {
  statuses: Record<MuscleId, MuscleStatus>;
  regions?: Region[];
}) {
  return (
    <div className="stack" style={{ gap: 12 }}>
      {regions.map((region) => {
        const muscles = (Object.keys(MUSCLES) as MuscleId[]).filter(
          (id) => MUSCLES[id].region === region,
        );
        return (
          <div key={region}>
            <div className="section-title" style={{ marginBottom: 6 }}>
              {REGION_LABEL[region]}
            </div>
            {muscles.map((id) => {
              const status = statuses[id];
              if (!status) return null;
              const pct = Math.min(100, Math.round(status.freshness * 100));
              return (
                <div className="recovery-row" key={id}>
                  <span className="name">{MUSCLES[id].short}</span>
                  <span className="bar">
                    <span style={{ width: `${pct}%`, background: barColor(status.freshness) }} />
                  </span>
                  <span className="pct">{label(status)}</span>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

/** Compact weekly-volume view: sets done vs the target range for each muscle. */
export function VolumeGrid({ statuses }: { statuses: Record<MuscleId, MuscleStatus> }) {
  const rows = (Object.keys(MUSCLES) as MuscleId[])
    .map((id) => statuses[id])
    .filter((s): s is MuscleStatus => Boolean(s))
    .sort((a, b) => b.weeklySets - a.weeklySets);

  return (
    <div>
      {rows.map((status) => {
        const [lo, hi] = status.target;
        const pct = Math.min(100, (status.weeklySets / hi) * 100);
        const color =
          status.volumeState === 'over'
            ? 'var(--danger)'
            : status.volumeState === 'in_range'
              ? 'var(--ok)'
              : 'var(--surface-3)';
        return (
          <div className="recovery-row" key={status.muscle}>
            <span className="name">{MUSCLES[status.muscle].short}</span>
            <span className="bar" title={`target ${lo}–${hi} sets/week`}>
              <span style={{ width: `${pct}%`, background: color }} />
            </span>
            <span className="pct">{status.weeklySets}</span>
          </div>
        );
      })}
      <div className="tiny faint" style={{ marginTop: 8 }}>
        Direct working sets over the last 7 days. Sets where a muscle is only a
        secondary mover count as a half. Bar fills to the top of the useful range.
      </div>
    </div>
  );
}
